import * as yup from 'yup';

import { determineIfPasswordIsTooRepetitive } from '@zocdoc-frontend-common/utils-password';
import {
    passwordLetters,
    passwordNumbers,
    passwordSymbols,
    passwordGuess,
    passwordLength,
    RULE_STATUS,
    passwordRepetitive,
    passwordWhiteSpace,
} from './constants';
import badPasswords from './badPasswords';

const validateEmail = email => {
    const schema = yup
        .string()
        .email()
        .required();
    return schema.isValid(email);
};
const validateConfirmEmail = (confirmEmail, email) => {
    if (email && confirmEmail) {
        return email.trim() === confirmEmail.trim();
    }
    return !(email && !confirmEmail);
};
// For Blizzard, if either email or confirm email field is empty
// confirm email Field is invalid and should be highlighted red
export const validateConfirmEmailBlizzard = (confirmEmail, email) => {
    if (email && confirmEmail) {
        return email.trim() === confirmEmail.trim();
    }
    return false;
};
const validateMonth = month => {
    const schema = yup
        .number()
        .min(1)
        .max(12)
        .required();
    return schema.isValid(Number(month));
};

const validateDay = day => {
    const schema = yup
        .number()
        .min(1)
        .max(31)
        .required();

    return schema.isValid(Number(day));
};

const validateYear = year => {
    const minDate = new Date();
    const maxYear = minDate.getFullYear() - 18;
    const minYear = minDate.getFullYear() - 120;
    const schema = yup
        .number()
        .max(maxYear)
        .min(minYear)
        .required();
    return schema.isValid(year);
};

/* On patient info page we handle the too young
 * error differently
 */
const validateYearForPatientInfo = year => {
    const minDate = new Date();
    const maxYear = minDate.getFullYear();
    const minYear = minDate.getFullYear() - 120;
    const schema = yup
        .number()
        .max(maxYear)
        .min(minYear)
        .required();
    return schema.isValid(year);
};

const validateBirthDate = date => {
    const [year, month, day] = date;

    const maxDate = new Date();
    const maxYear = maxDate.getFullYear() - 18;

    maxDate.setFullYear(maxYear);
    const schema = yup.date().max(maxDate);

    return schema.isValid(new Date(year, month, day));
};

const validateName = name => {
    const schema = yup
        .string()
        .min(1)
        .required();
    return schema.isValid(name);
};
const validateGender = gender => {
    const schema = yup
        .mixed()
        .oneOf(['male', 'female'])
        .required();
    return schema.isValid(gender);
};
const validateTerms = accepted => {
    const schema = yup
        .bool()
        .required()
        .oneOf([true]);
    return schema.isValid(accepted);
};
const passwordSchema = yup
    .string()
    .matches(/\d/, passwordNumbers)
    .matches(/[a-zA-Z]/, passwordLetters)
    .matches(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/, passwordSymbols)
    .min(8, passwordLength)
    .test(passwordGuess, passwordGuess, password =>
        badPasswords.every(badPassword => password.indexOf(badPassword) === -1)
    )
    .test(
        passwordWhiteSpace,
        passwordWhiteSpace,
        password => password.indexOf(' ') === -1
    )
    .test(
        passwordRepetitive,
        passwordRepetitive,
        password => !determineIfPasswordIsTooRepetitive(password)
    )
    .required()
    .ensure();

const validatePassword = password => {
    return passwordSchema.isValid(password);
};

export const passwordStatusCheck = password => {
    const statuses = {
        [passwordLength]: RULE_STATUS.VALID,
        [passwordLetters]: RULE_STATUS.VALID,
        [passwordSymbols]: RULE_STATUS.VALID,
        [passwordNumbers]: RULE_STATUS.VALID,
        [passwordGuess]: RULE_STATUS.VALID,
        [passwordRepetitive]: RULE_STATUS.VALID,
        [passwordWhiteSpace]: RULE_STATUS.VALID,
    };

    return passwordSchema
        .validate(password, { abortEarly: false })
        .then(() => statuses)
        .catch(e => {
            const update = e.errors.reduce((acc, cur) => {
                acc[cur] = RULE_STATUS.INVALID;
                return acc;
            }, {});

            return {
                ...statuses,
                ...update,
            };
        });
};

const getValidator = field => {
    return {
        email: validateEmail,
        confirmEmail: validateConfirmEmail,
        birthDay: validateDay,
        birthMonth: validateMonth,
        birthDate: validateBirthDate,
        birthYear: validateYear,
        firstName: validateName,
        lastName: validateName,
        gender: validateGender,
        termsOfService: validateTerms,
        password: validatePassword,
        passwordStatusCheck: passwordStatusCheck,
    }[field];
};

/* This is a bit hacky but if Blizzard wins, we will tear
 * down the original validateYear and if it loses we will
 * tear down validateYearForPatientInfo.
 */
export const getValidatorForPatientInfo = field =>
    field === 'birthYear' ? validateYearForPatientInfo : getValidator(field);

export default getValidator;
